import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "elite_access_token";

// Token storage uses sessionStorage (cleared on tab close, less XSS-exposed than localStorage).
// Primary auth mechanism remains httpOnly cookies set by the backend.
export const tokenStore = {
  get: () => {
    try { return sessionStorage.getItem(TOKEN_KEY); }
    catch (e) { console.warn("tokenStore.get:", e); return null; }
  },
  set: (t) => {
    try { sessionStorage.setItem(TOKEN_KEY, t); }
    catch (e) { console.warn("tokenStore.set:", e); }
  },
  clear: () => {
    try { sessionStorage.removeItem(TOKEN_KEY); }
    catch (e) { console.warn("tokenStore.clear:", e); }
  },
};

// ---------------------------------------------------------------------------
// Durée de vie ABSOLUE de la session — 24 h après le login (perrinos.tech).
//
// L'access token ne vit que 10 minutes et /auth/refresh renouvelle le couple
// tant que le refresh cookie est valide : sans échéance explicite, un onglet
// laissé ouvert restait connecté plusieurs jours. On mémorise donc ici la
// date limite (localStorage, partagée entre les onglets) :
//   - AuthContext arme un timer sur cette échéance → déconnexion instantanée
//     suivie de la fenêtre de connexion, même sans activité dans l'onglet ;
//   - l'intercepteur refuse tout renouvellement passé cette limite ;
//   - le backend applique la même borne (claim `auth_time`) : il reste la
//     source de vérité si le navigateur retarde ou saute le timer.
// ---------------------------------------------------------------------------
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000;
const DEADLINE_KEY = "elite_session_deadline";

// Émis quand le serveur refuse de renouveler la session (401 sur
// /auth/refresh) ou quand l'échéance absolue est atteinte : AuthContext écoute
// cet événement pour couper la session et basculer sur /login.
export const SESSION_EXPIRED_EVENT = "perrinos:session-expired";

function decodeJwtClaims(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch (e) {
    return null;
  }
}

// Échéance théorique d'une session venant d'être ouverte avec ce token :
// `auth_time` (horodatage du login côté serveur) + 24 h, sinon maintenant + 24 h.
function deadlineForToken(token) {
  const claims = decodeJwtClaims(token);
  const authTime = claims && typeof claims.auth_time === "number" ? claims.auth_time * 1000 : null;
  return (authTime != null ? authTime : Date.now()) + SESSION_TTL_MS;
}

export const sessionStore = {
  // Au login : pose l'échéance de la nouvelle session (remplace la précédente).
  start(atMs = Date.now() + SESSION_TTL_MS) {
    try { localStorage.setItem(DEADLINE_KEY, String(atMs)); }
    catch (e) { console.warn("sessionStore.start:", e); }
    return atMs;
  },
  // Aligne l'échéance sur le token courant SANS jamais la repousser :
  // une rotation de token ne prolonge pas la session.
  sync(token) {
    const candidate = deadlineForToken(token);
    const current = sessionStore.deadline();
    if (current != null && current <= candidate) return current;
    return sessionStore.start(candidate);
  },
  deadline() {
    try {
      const raw = localStorage.getItem(DEADLINE_KEY);
      if (raw == null) return null;
      const value = Number(raw);
      return Number.isFinite(value) ? value : null;
    } catch (e) {
      return null;
    }
  },
  isExpired() {
    const deadline = sessionStore.deadline();
    return deadline != null && Date.now() >= deadline;
  },
  clear() {
    try { localStorage.removeItem(DEADLINE_KEY); }
    catch (e) { console.warn("sessionStore.clear:", e); }
  },
};

function signalSessionExpired() {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Bearer fallback (cookies remain the source of truth when same-site)
api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Silent session refresh (single-flight).
// The access token lives 10 minutes; a tab left open past its expiry would
// fail every call with 401 "Token expiré" even though the refresh cookie is
// still valid. We renew the pair once through /auth/refresh, then retry the
// original request with the fresh token. Renewal stops dead at the absolute
// 24h session deadline (see sessionStore above).
let refreshPromise = null;

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api
      .post("/auth/refresh", {}, { withCredentials: true })
      .then((res) => {
        const t = res.data?.access_token;
        if (t) tokenStore.set(t);
        return res.data;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Paths where a 401 is a legitimate answer (bad password, dead session…)
// and must not trigger a refresh loop.
const NO_REFRESH_PATHS = /\/auth\/(login|register|refresh|logout|google\/session|set-role)(\?|$)/;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    // Trace locale d'une session déjà ouverte : un Bearer en sessionStorage
    // ou l'échéance absolue des 24 h. Vide = visiteur anonyme, dont un 401
    // n'est pas une expiration.
    const hadSession = Boolean(tokenStore.get()) || sessionStore.deadline() != null;
    const isAuthFailure =
      error.response?.status === 401 &&
      !original._authRetried &&
      !NO_REFRESH_PATHS.test(original.url || "");

    if (isAuthFailure) {
      // Échéance absolue atteinte : inutile de demander un renouvellement,
      // le backend refuserait de toute façon. On coupe la session sur-le-champ.
      if (sessionStore.isExpired()) {
        tokenStore.clear();
        sessionStore.clear();
        signalSessionExpired();
        if (error.response?.data && typeof error.response.data === "object") {
          error.response.data.detail = "Session expirée — reconnectez-vous pour continuer.";
        }
        return Promise.reject(error);
      }
      original._authRetried = true;
      try {
        await refreshSession();
        const t = tokenStore.get();
        if (t) sessionStore.sync(t);
        if (t && original.headers) {
          if (typeof original.headers.set === "function") {
            original.headers.set("Authorization", `Bearer ${t}`);
          } else {
            original.headers.Authorization = `Bearer ${t}`;
          }
        }
        return api.request(original);
      } catch (refreshError) {
        // Renewal impossible → the session is really over: drop the stale
        // Bearer and surface a message the user can act on.
        tokenStore.clear();
        sessionStore.clear();
        // Un 401 du serveur = session vraiment terminée (24 h atteintes,
        // cookies purgés…) : on prévient l'app pour qu'elle déconnecte et
        // affiche la fenêtre de connexion. Une erreur réseau reste
        // transitoire et ne déconnecte pas pour autant.
        //
        // **Uniquement s'il y avait une session à perdre.** Au premier
        // chargement d'un visiteur anonyme, `/auth/me` répond 401 et le
        // renouvellement aussi : sans cette garde, chaque arrivée sur /login
        // annonçait « Session expirée après 24 h » à quelqu'un qui n'a jamais
        // été connecté. La tentative de renouvellement, elle, reste faite —
        // un onglet neuf peut n'avoir que les cookies (le token vit en
        // sessionStorage) et mérite d'être rétabli.
        if (refreshError?.response?.status === 401 && hadSession) signalSessionExpired();
        if (error.response?.data && typeof error.response.data === "object") {
          error.response.data.detail = "Session expirée — reconnectez-vous pour continuer.";
        }
      }
    }
    return Promise.reject(error);
  }
);

function formatDetailValue(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const DEFAULT_ERROR_FALLBACK = "Une erreur est survenue. Veuillez réessayer.";

// Accepts either a raw `detail` value (legacy call sites,
// formatApiErrorDetail(e.response?.data?.detail)) or a full axios error
// (formatApiErrorDetail(e, "fallback")). For an axios error it also checks
// `data.error` (the shape slowapi's rate-limit handler returns — it has no
// `detail` field) and finally `e.message` (network/timeout errors), so a
// real cause is never silently swallowed behind the generic fallback.
export function formatApiErrorDetail(errorOrDetail, fallback = DEFAULT_ERROR_FALLBACK) {
  const isAxiosError =
    errorOrDetail != null &&
    typeof errorOrDetail === "object" &&
    ("response" in errorOrDetail || "isAxiosError" in errorOrDetail || errorOrDetail instanceof Error);

  if (!isAxiosError) {
    return formatDetailValue(errorOrDetail) || fallback;
  }

  const data = errorOrDetail.response?.data;
  const formattedDetail = formatDetailValue(data?.detail);
  return formattedDetail || data?.error || errorOrDetail.message || fallback;
}
